import AddIcon from "@mui/icons-material/Add";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import UiTypography from "components/base/UiTypography";
import type { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";
import type { ProductOption } from "../measurementTypes";
import type { AiAssignmentPersistence, AiSavedAssignment, CostOption } from "./aiAssignmentTypes";
import ProductDraftCard from "./ProductDraftCard";
import useProductDrafts from "./useProductDrafts";

export type { AiAssignmentPersistence, AiSavedAssignment } from "./aiAssignmentTypes";

export default function AiProductAssignments({
  sources,
  products,
  additionalCosts,
  savedAiAssignments = [],
  persistence,
  onUpdateMapMeasurementProduct,
}: {
  sources: AssignmentSource[];
  products: ProductOption[];
  additionalCosts: CostOption[];
  savedAiAssignments?: AiSavedAssignment[];
  persistence?: AiAssignmentPersistence;
  onUpdateMapMeasurementProduct: (featureId: string, product: ProductOption | null) => void;
}) {
  const {
    drafts,
    compactProducts,
    sourceById,
    availableSources,
    isLinearFeetAllowed,
    addDraft,
    toggleOpen,
    addSource,
    removeJobSource,
    removeCostSource,
    changeProduct,
    changeAdditionalCost,
    removeAdditionalCost,
    removeProduct,
    changeStallMode,
    addCostRow,
  } = useProductDrafts({
    sources,
    products,
    savedAiAssignments,
    persistence,
    onUpdateMapMeasurementProduct,
  });

  return (
    <MDBox>
      <MDBox mb="10px">
        <UiTypography uiVariant="heading-sm">Measurements</UiTypography>
        <UiTypography uiVariant="caption" sx={{ color: "#667085" }}>
          Assign map measurements, Counts, and Measured Areas to products and additional costs.
        </UiTypography>
      </MDBox>

      {!sources.length && (
        <UiTypography uiVariant="body-sm" sx={{ color: "#667085", mb: "12px" }}>
          Draw a measurement or run AI mapping to begin assigning products.
        </UiTypography>
      )}

      <MDBox display="flex" flexDirection="column" gap="10px">
        {drafts.map((draft) => (
          <ProductDraftCard
            key={draft.id}
            draft={draft}
            products={compactProducts}
            sources={availableSources}
            additionalCosts={additionalCosts}
            sourceById={sourceById}
            isLinearFeetAllowed={isLinearFeetAllowed}
            onToggleOpen={() => toggleOpen(draft.id)}
            onChangeProduct={(productId) => changeProduct(draft.id, productId)}
            onAddSource={(costId, sourceId) => addSource(draft.id, costId, sourceId)}
            onRemoveJobSource={(assignment) => removeJobSource(draft.id, assignment)}
            onRemoveCostSource={(costId, assignment) =>
              removeCostSource(draft.id, costId, assignment)
            }
            onChangeAdditionalCost={(costRowId, additionalCostId) =>
              changeAdditionalCost(draft.id, costRowId, additionalCostId)
            }
            onRemoveAdditionalCost={(costRowId) => removeAdditionalCost(draft.id, costRowId)}
            onRemoveProduct={() => removeProduct(draft.id)}
            onChangeStallMode={(assignmentId, mode, costId) =>
              changeStallMode(draft.id, assignmentId, mode, costId)
            }
            onAddCostRow={() => addCostRow(draft.id)}
          />
        ))}
      </MDBox>

      <MDButton
        size="small"
        variant="outlined"
        color="info"
        startIcon={<AddIcon />}
        onClick={addDraft}
        sx={{ mt: "12px" }}
      >
        Add Product
      </MDButton>
    </MDBox>
  );
}
